// src/pages/Dashboard.js
//
// Dashboard (My Page / Inbox)
// ===========================
//
// ARCHITECTURE CONTRACT
// ---------------------
// This page follows the current application architecture:
//
// 1) HTTP is authoritative
//    - dashboard content is rendered ONLY from GET /my-page
//    - local optimistic updates are allowed only as temporary UX helpers
//    - final truth always comes from the next /my-page fetch
//
// 2) Sockets are invalidation-only
//    - dashboard listens primarily to "myPageInvalidated"
//    - after invalidation, dashboard refetches /my-page
//
// 3) Authentication behavior
//    - explicit user logout happens elsewhere (Navbar / AuthContext.handleLogout)
//    - page-level 401 must use softLogout()
//    - page must NEVER call backend /auth/logout on its own
//
// 4) Session model
//    - same user + same browser/device should share one backend auth session
//    - this tab may still fail independently (e.g. selector missing / refresh failed)
//    - therefore page-level failures must stay tab-local
//
// UX NOTES
// --------
// - Leave items can expose workflow actions (approve / reject)
// - ToDo items can be marked as done directly from Dashboard
// - Quick Create supports both ToDo creation and opening Leave Request modal

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import AuthContext from "../context/AuthContext";

import { fetchUsers } from "../services/userService";
import { createTodo, markTodoDone } from "../services/todoService";
import { fetchMyPage } from "../services/myPageService";
import { decideLeaveRequest } from "../services/leaveRequestsService";
import { isRequestCanceled } from "../services/httpUtils";

import socketClient from "../utils/socketClient";
import { SOCKET_EVENTS } from "../utils/socketEvents";

import LeaveRequestCreateModal from "../components/leave/LeaveRequestCreateModal";

import "./Dashboard.css";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const DEFAULT_COUNTERS = Object.freeze({
  inbox_open: 0,
  overdue: 0,
  unread_chat: 0,
  new_inbox: 0,
});

/**
 * How long dashboard waits before hiding transient success/error messages.
 */
const MESSAGE_CLEAR_DELAY_MS = 3000;

/**
 * Debounce used for socket-triggered /my-page refresh.
 * A short delay helps collapse bursts of invalidation events into one fetch.
 */
const INVALIDATION_REFRESH_DELAY_MS = 300;

/* -------------------------------------------------------------------------- */
/* Small pure helpers                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Return a friendly error message.
 *
 * Strategy:
 * - for 4xx with backend message -> show backend message
 * - otherwise -> use fallback
 */
function getNiceErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  const status = err?.response?.status;
  const backendMsg = err?.response?.data?.message || err?.response?.data?.error || "";

  if (typeof status === "number" && status >= 400 && status < 500 && backendMsg) {
    return String(backendMsg);
  }

  return fallback;
}

/**
 * Clear a timeout stored in a ref.
 */
function clearTimerRef(timerRef) {
  if (timerRef?.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}

/**
 * Parse "todo:<id>" work item key.
 *
 * Returns:
 * - integer id
 * - or null when the key is not a valid todo key
 */
function parseTodoIdFromWorkItemKey(workItemKey) {
  const key = String(workItemKey || "");
  const [kind, idStr] = key.split(":");

  if (kind !== "todo") return null;

  const id = Number(idStr);
  if (!Number.isFinite(id) || !Number.isInteger(id) || id <= 0) return null;

  return id;
}

/**
 * Parse "leave:<id>" work item key.
 *
 * IMPORTANT:
 * - leave ids may be BIGINT on backend
 * - therefore we keep them as strings
 */
function parseLeaveIdFromWorkItemKey(workItemKey) {
  const key = String(workItemKey || "");
  const [kind, idStr] = key.split(":");

  if (kind !== "leave") return null;
  if (!idStr || !/^[0-9]{1,20}$/.test(idStr)) return null;

  return idStr;
}

/**
 * Map leave action hints to CSS-friendly suffix.
 */
function actionHintToCss(actionHint) {
  return String(actionHint || "NONE").toLowerCase();
}

/**
 * Normalize backend /my-page payload to stable dashboard state.
 */
function normalizeMyPagePayload(myPage) {
  return {
    counters: myPage?.counters || DEFAULT_COUNTERS,
    inbox: Array.isArray(myPage?.inbox) ? myPage.inbox : [],
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const Dashboard = () => {
  const { t } = useTranslation(["common", "dashboard"]);
  const navigate = useNavigate();

  const {
    authState,
    authChecked,
    softLogout,
  } = useContext(AuthContext);

  /* ------------------------------------------------------------------------ */
  /* Local component state                                                    */
  /* ------------------------------------------------------------------------ */

  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [counters, setCounters] = useState(DEFAULT_COUNTERS);
  const [inbox, setInbox] = useState([]);

  const [newTodo, setNewTodo] = useState({
    description: "",
    assignedTo: "",
    is_private: false,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  /* ------------------------------------------------------------------------ */
  /* Refs                                                                     */
  /* ------------------------------------------------------------------------ */

  const isMountedRef = useRef(false);
  const initialLoadAbortRef = useRef(null);
  const refreshAbortRef = useRef(null);
  const messageTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  /**
   * Prevent socket/focus background refreshes from racing the first normal page
   * load.  The initial load already fetches /my-page authoritatively and owns
   * the main loading spinner.  Reconnect/invalidation refreshes become active
   * after the first successful payload has been applied.
   */
  const hasInitialMyPageLoadedRef = useRef(false);

  /* ------------------------------------------------------------------------ */
  /* Lifecycle / mount safety                                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (initialLoadAbortRef.current) {
        initialLoadAbortRef.current.abort();
      }

      if (refreshAbortRef.current) {
        refreshAbortRef.current.abort();
      }

      clearTimerRef(messageTimerRef);
      clearTimerRef(refreshTimerRef);
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Message helpers                                                          */
  /* ------------------------------------------------------------------------ */

  /**
   * Auto-clear transient page messages after a short delay.
   */
  const scheduleClearMessages = useCallback(() => {
    clearTimerRef(messageTimerRef);

    messageTimerRef.current = setTimeout(() => {
      messageTimerRef.current = null;

      if (!isMountedRef.current) return;

      setError("");
      setSuccessMessage("");
    }, MESSAGE_CLEAR_DELAY_MS);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Local state application helper                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * Apply authoritative dashboard payload to component state.
   */
  const applyMyPageData = useCallback((myPage) => {
    if (!isMountedRef.current) return;

    const normalized = normalizeMyPagePayload(myPage);

    setCounters(normalized.counters);
    setInbox(normalized.inbox);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Authoritative background refresh of /my-page                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Refresh dashboard data from the authoritative backend endpoint.
   *
   * IMPORTANT:
   * - this does NOT change the main `loading` spinner state
   * - it is meant for background refreshes after socket invalidations or mutations
   */
  const refreshMyPage = useCallback(async () => {
    if (!authChecked || !authState.isAuthenticated) {
      return;
    }

    if (refreshAbortRef.current) {
      refreshAbortRef.current.abort();
    }

    const controller = new AbortController();
    refreshAbortRef.current = controller;

    try {
      const myPage = await fetchMyPage({ signal: controller.signal });

      if (controller.signal.aborted || !isMountedRef.current) {
        return;
      }

      applyMyPageData(myPage);
    } catch (err) {
      if (isRequestCanceled(err) || controller.signal.aborted) {
        return;
      }

      /**
       * IMPORTANT:
       * 401 here must remain tab-local.
       * We do NOT hard logout the whole browser session.
       */
      if (err?.response?.status === 401) {
        softLogout("DASHBOARD_REFRESH_401");
        return;
      }

      console.error("[Dashboard] refreshMyPage failed:", err);

      if (!isMountedRef.current) return;

      setError(t("dashboard:messages.refreshFailed"));
      scheduleClearMessages();
    }
  }, [authChecked, authState.isAuthenticated, applyMyPageData, softLogout, scheduleClearMessages, t]);

  /**
   * Debounced wrapper used after socket invalidation bursts.
   */
  const scheduleRefreshMyPage = useCallback((delayMs = INVALIDATION_REFRESH_DELAY_MS) => {
    if (!authChecked || !authState.isAuthenticated) {
      return;
    }

    if (!hasInitialMyPageLoadedRef.current) {
      return;
    }

    clearTimerRef(refreshTimerRef);

    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;

      if (!isMountedRef.current) return;

      refreshMyPage();
    }, delayMs);
  }, [authChecked, authState.isAuthenticated, refreshMyPage]);

  /* ------------------------------------------------------------------------ */
  /* Socket subscriptions                                                     */
  /* ------------------------------------------------------------------------ */

  /**
   * Dashboard listens only to invalidation events.
   *
   * This is the cleaner fit for your current architecture:
   * - socket tells us "something changed"
   * - dashboard refetches /my-page
   *
   * We intentionally do NOT rely on work-item chat events here for correctness.
   * Those events belong more naturally to detailed work-item/chat views.
   */
  useEffect(() => {
    if (!authChecked || !authState.isAuthenticated) {
      return undefined;
    }

    /**
     * Register listeners before connect().  connect() is asynchronous in the
     * normal case, but registering first avoids tiny ordering gaps and keeps the
     * reconnect path deterministic after a backend restart.
     */
    const offConnect = socketClient.on(SOCKET_EVENTS.CONNECT, () => {
      if (DEV) {
        // eslint-disable-next-line no-console
        console.log("[Dashboard] socket connected/reconnected -> refresh /my-page");
      }

      /**
       * Socket.IO is not a durable queue.  After every successful reconnect we
       * perform one silent REST synchronization.  This is not polling and does
       * not switch the page back to the main loading state, so it should not
       * cause UI flicker.
       */
      scheduleRefreshMyPage(0);
    });

    const offInvalidated = socketClient.on(
      SOCKET_EVENTS.MY_PAGE_INVALIDATED,
      (payload) => {
        if (DEV) {
          // eslint-disable-next-line no-console
          console.log("[Dashboard] myPageInvalidated:", payload);
        }

        scheduleRefreshMyPage(INVALIDATION_REFRESH_DELAY_MS);
      }
    );

    socketClient.connect();

    return () => {
      offConnect?.();
      offInvalidated?.();

      /**
       * IMPORTANT:
       * We intentionally do NOT call socketClient.disconnect() here.
       *
       * Why:
       * - socket lifecycle is app-level-ish in your current frontend
       * - other pages may also depend on the same singleton connection
       * - auth cleanup / explicit logout is the right place to disconnect fully
       */
    };
  }, [authChecked, authState.isAuthenticated, scheduleRefreshMyPage]);

  /**
   * Lightweight focus/visibility recovery.
   *
   * This is intentionally event-driven only.  There is no interval, no polling
   * loop and no custom healthcheck.  When a user returns to a tab after the
   * backend was restarted or the network changed, we simply ensure the socket
   * still wants to be connected and perform one silent /my-page refresh.
   */
  useEffect(() => {
    if (!authChecked || !authState.isAuthenticated) {
      return undefined;
    }

    const refreshWhenTabBecomesActive = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      /**
       * Keep this recovery intentionally small.
       *
       * Earlier experimental versions tried to actively verify/rebuild the
       * socket from focus events. That created a lot of diagnostics and did not
       * solve the backend-restart edge case reliably. The minimal, stable
       * behavior is to ask the singleton socket to connect if it is not already
       * connected and then refresh the authoritative /my-page snapshot below.
       */
      socketClient.connect();

      /**
       * Keep /my-page as the authoritative source of truth.  This single silent
       * refresh is the existing focus/visibility fallback and is deliberately
       * event-driven only.
       */
      scheduleRefreshMyPage(INVALIDATION_REFRESH_DELAY_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshWhenTabBecomesActive();
      }
    };

    window.addEventListener("focus", refreshWhenTabBecomesActive);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshWhenTabBecomesActive);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authChecked, authState.isAuthenticated, scheduleRefreshMyPage]);

  /* ------------------------------------------------------------------------ */
  /* Initial page load                                                        */
  /* ------------------------------------------------------------------------ */

  /**
   * Initial load for:
   * - users list (for quick ToDo create)
   * - authoritative /my-page payload
   *
   * This runs only when:
   * - auth bootstrap is complete
   * - user is authenticated
   *
   * We intentionally do NOT call initializeAuthState() from here anymore.
   * That belongs to AuthContext + ProtectedRoute.
   */
  useEffect(() => {
    if (!authChecked || !authState.isAuthenticated) {
      return undefined;
    }

    if (initialLoadAbortRef.current) {
      initialLoadAbortRef.current.abort();
    }

    const controller = new AbortController();
    initialLoadAbortRef.current = controller;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        /**
         * User list may be unavailable for some roles.
         * 403 here should not break the whole dashboard.
         */
        const usersPromise = fetchUsers({ signal: controller.signal }).catch((err) => {
          if (isRequestCanceled(err)) return [];
          if (err?.response?.status === 403) return [];
          throw err;
        });

        const myPagePromise = fetchMyPage({ signal: controller.signal });

        const [usersData, myPage] = await Promise.all([usersPromise, myPagePromise]);

        if (controller.signal.aborted || !isMountedRef.current) {
          return;
        }

        setUsers(Array.isArray(usersData) ? usersData : []);
        applyMyPageData(myPage);
        hasInitialMyPageLoadedRef.current = true;
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) {
          return;
        }

        if (err?.response?.status === 401) {
          softLogout("DASHBOARD_INITIAL_LOAD_401");
          return;
        }

        console.error("[Dashboard] initial load failed:", err);

        if (!isMountedRef.current) return;

        setError(t("dashboard:messages.loadFailed"));
        scheduleClearMessages();
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      controller.abort();
    };
  }, [authChecked, authState.isAuthenticated, applyMyPageData, softLogout, scheduleClearMessages, t]);

  /* ------------------------------------------------------------------------ */
  /* ToDo mutations                                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * Create a new ToDo from the quick-create panel.
   */
  const handleCreateTodo = useCallback(async () => {
    const description = String(newTodo.description || "").trim();
    const assigneeId = Number(newTodo.assignedTo);

    if (!description || !Number.isFinite(assigneeId) || assigneeId <= 0) {
      setError(t("dashboard:messages.todoRequired"));
      scheduleClearMessages();
      return;
    }

    try {
      await createTodo({
        description,
        is_private: Boolean(newTodo.is_private),
        assignee_id: assigneeId,
      });

      setNewTodo({
        description: "",
        assignedTo: "",
        is_private: false,
      });

      setSuccessMessage(t("dashboard:messages.todoCreated"));

      /**
       * Use authoritative refresh after mutation.
       * The backend already emits invalidations too, but direct refresh here
       * gives faster UX and keeps this mutation self-contained.
       */
      await refreshMyPage();
    } catch (err) {
      if (isRequestCanceled(err)) return;

      if (err?.response?.status === 401) {
        softLogout("DASHBOARD_CREATE_TODO_401");
        return;
      }

      console.error("[Dashboard] failed to create To-Do:", err);
      setError(getNiceErrorMessage(err, t("dashboard:messages.todoCreateFailed")));
    } finally {
      scheduleClearMessages();
    }
  }, [newTodo, refreshMyPage, softLogout, scheduleClearMessages, t]);

  /**
   * Mark a ToDo as done directly from dashboard.
   *
   * We keep a tiny optimistic update:
   * - remove the item from local inbox immediately
   * - then refresh authoritatively
   *
   * If the request fails, we restore via authoritative refresh.
   */
  const handleMarkDone = useCallback(async (item) => {
    const todoId = parseTodoIdFromWorkItemKey(item?.key);

    if (!todoId) {
      setError(t("dashboard:messages.cannotMarkDone"));
      scheduleClearMessages();
      return;
    }

    const previousInbox = inbox;
    const previousCounters = counters;

    // Small optimistic removal for snappier UX
    setInbox((list) => list.filter((x) => x.key !== item.key));
    setCounters((current) => ({
      ...current,
      inbox_open: Math.max(0, Number(current.inbox_open || 0) - 1),
    }));

    try {
      await markTodoDone(todoId);

      setSuccessMessage(t("dashboard:messages.markedDone"));

      // Quick authoritative sync
      scheduleRefreshMyPage(150);
    } catch (err) {
      if (isRequestCanceled(err)) return;

      if (err?.response?.status === 401) {
        softLogout("DASHBOARD_MARK_DONE_401");
        return;
      }

      console.error("[Dashboard] failed to mark ToDo as done:", err);

      setError(getNiceErrorMessage(err, t("dashboard:messages.markDoneFailed")));
      setInbox(previousInbox);
      setCounters(previousCounters);

      await refreshMyPage();
    } finally {
      scheduleClearMessages();
    }
  }, [inbox, counters, refreshMyPage, softLogout, scheduleRefreshMyPage, scheduleClearMessages, t]);

  /* ------------------------------------------------------------------------ */
  /* Leave quick actions                                                      */
  /* ------------------------------------------------------------------------ */

  /**
   * Quick approve/reject action for leave workflow items.
   */
  const quickDecideLeave = useCallback(async (workItemKey, decision) => {
    const leaveId = parseLeaveIdFromWorkItemKey(workItemKey);
    if (!leaveId) return;

    try {
      await decideLeaveRequest(leaveId, decision, null);

      setSuccessMessage(decision === "APPROVED" ? t("dashboard:messages.approved") : t("dashboard:messages.rejected"));
      scheduleRefreshMyPage(150);
    } catch (err) {
      if (err?.response?.status === 401) {
        softLogout("DASHBOARD_QUICK_DECIDE_401");
        return;
      }

      console.error("[Dashboard] quick leave decision failed:", err);
      setError(getNiceErrorMessage(err, t("dashboard:messages.decisionFailed")));
    } finally {
      scheduleClearMessages();
    }
  }, [scheduleRefreshMyPage, scheduleClearMessages, softLogout, t]);

  /* ------------------------------------------------------------------------ */
  /* Navigation                                                               */
  /* ------------------------------------------------------------------------ */

  /**
   * Open work item details page.
   */
  const openWorkItem = useCallback((item) => {
    navigate(`/work-items/${encodeURIComponent(item.key)}`);
  }, [navigate]);

  /**
   * Open work item chat page.
   */
  const openWorkItemChat = useCallback((item) => {
    navigate(`/work-items/${encodeURIComponent(item.key)}/chat`);
  }, [navigate]);

  /* ------------------------------------------------------------------------ */
  /* Leave modal callback                                                     */
  /* ------------------------------------------------------------------------ */

  /**
   * Called after LeaveRequestCreateModal completes successfully.
   *
   * We always refresh dashboard authoritatively.
   * If a concrete work item key exists, we navigate to that item.
   */
  const onLeaveCreated = useCallback((payload) => {
    scheduleRefreshMyPage(0);

    const key = payload?.work_item_key || (payload?.id ? `leave:${payload.id}` : null);

    if (key) {
      navigate(`/work-items/${encodeURIComponent(key)}`);
      return;
    }

    // Some cancellation flows may complete without returning a standard key.
    setSuccessMessage(t("dashboard:messages.operationCompleted"));
    scheduleClearMessages();
  }, [navigate, scheduleRefreshMyPage, scheduleClearMessages, t]);

  /* ------------------------------------------------------------------------ */
  /* Derived UI state                                                         */
  /* ------------------------------------------------------------------------ */

  const shouldPulse =
    Number(counters.unread_chat || 0) > 0 ||
    Number(counters.new_inbox || 0) > 0;

  const visibleInbox = useMemo(() => inbox || [], [inbox]);

  /* ------------------------------------------------------------------------ */
  /* Render guards                                                            */
  /* ------------------------------------------------------------------------ */

  /**
   * In normal flow, ProtectedRoute should already ensure we render this page
   * only after auth bootstrap and only for authenticated users.
   *
   * These guards are still kept as defensive fallbacks.
   */
  if (!authChecked) {
    return (
      <div className="mypage-container">
        <div style={{ padding: 16 }}>{t("dashboard:loading")}</div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return (
      <div className="mypage-container">
        <p>{t("dashboard:notAuthenticated")}</p>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="mypage-container" role="main" aria-label={t("dashboard:ariaLabel")}>
      <LeaveRequestCreateModal
        open={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        currentUserId={authState?.user?.id}
        onCreated={onLeaveCreated}
      />

      <div className="mypage-header">
        <div className="mypage-header-left">
          <div className={`pulse-dot ${shouldPulse ? "" : "pulse-off"}`} aria-hidden="true" />
          <div>
            <h2 className="mypage-title">{t("dashboard:title")}</h2>
            <div className="mypage-subtitle">
              {loading ? t("dashboard:loading") : t("dashboard:itemsWaiting", { count: counters.inbox_open || 0 })}
            </div>
          </div>
        </div>

        <div className="mypage-header-right">
          <div className="counter-chip">
            <div className="counter-label">{t("dashboard:counters.open")}</div>
            <div className="counter-value">{counters.inbox_open ?? 0}</div>
          </div>

          <div className="counter-chip subtle">
            <div className="counter-label">{t("dashboard:counters.new")}</div>
            <div className="counter-value">{counters.new_inbox ?? 0}</div>
          </div>

          <div className="counter-chip subtle">
            <div className="counter-label">{t("dashboard:counters.chatUnread")}</div>
            <div className="counter-value">{counters.unread_chat ?? 0}</div>
          </div>

          <div className="counter-chip subtle">
            <div className="counter-label">{t("dashboard:counters.overdue")}</div>
            <div className="counter-value">{counters.overdue ?? 0}</div>
          </div>
        </div>
      </div>

      <div className="mypage-messages" aria-live="polite">
        {error && <div className="msg error">{error}</div>}
        {successMessage && <div className="msg success">{successMessage}</div>}
      </div>

      <div className="mypage-grid">
        <div className="inbox-panel">
          <div className="panel-title">{t("dashboard:inbox.title")}</div>

          {loading ? (
            <div className="panel-hint">{t("dashboard:inbox.loading")}</div>
          ) : visibleInbox.length === 0 ? (
            <div className="panel-hint">
              {t("dashboard:inbox.empty")}
              <div className="panel-hint-sub">
                {t("dashboard:inbox.emptySub")}
              </div>
            </div>
          ) : (
            <div className="inbox-list" role="list" aria-label={t("dashboard:inbox.listAriaLabel")}>
              {visibleInbox.map((item) => {
                const isLeave = item.type === "leave";
                const blink = Boolean(item.isNewInInbox);

                const actionHint = isLeave ? (item?.meta?.actionHint || "NONE") : "NONE";
                const leaveBadgeText = isLeave
                  ? (item?.meta?.badge || item?.meta?.status || "Leave")
                  : null;

                const leaveBadgeClass = isLeave
                  ? `leave-badge leave-badge--${actionHintToCss(actionHint)}`
                  : "";

                return (
                  <div
                    key={item.key}
                    className={`inbox-item ${blink ? "blink" : ""}`}
                    onClick={() => openWorkItem(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        openWorkItem(item);
                      }
                    }}
                    title={t("dashboard:inbox.openItemTitle")}
                  >
                    <div className="inbox-item-main">
                      <div className="inbox-item-title">
                        {item.title}

                        {isLeave && leaveBadgeText && (
                          <span
                            className={leaveBadgeClass}
                            title={t("dashboard:inbox.leaveWorkflowTitle", { actionHint })}
                          >
                            {leaveBadgeText}
                          </span>
                        )}
                      </div>

                      {item.summary && (
                        <div className="inbox-item-summary">{item.summary}</div>
                      )}
                    </div>

                    <div className="inbox-item-meta">
                      <button
                        type="button"
                        className={`badge chat-badge ${item.hasUnreadChat ? "chat-unread" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openWorkItemChat(item);
                        }}
                        title={t("dashboard:inbox.openChatTitle")}
                      >
                        💬 {Number(item.commentsCount || 0)}
                      </button>

                      {item.isNewInInbox && <div className="badge new">{t("dashboard:inbox.newBadge")}</div>}

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openWorkItem(item);
                        }}
                      >
                        {t("dashboard:inbox.open")}
                      </button>

                      {!isLeave && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkDone(item);
                          }}
                          title={t("dashboard:inbox.markDoneTitle")}
                        >
                          {t("dashboard:inbox.done")}
                        </button>
                      )}

                      {isLeave && actionHint === "APPROVE_REJECT" && (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              quickDecideLeave(item.key, "APPROVED");
                            }}
                            title={t("dashboard:inbox.approveTitle")}
                          >
                            {t("dashboard:inbox.approve")}
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              quickDecideLeave(item.key, "REJECTED");
                            }}
                            title={t("dashboard:inbox.rejectTitle")}
                          >
                            {t("dashboard:inbox.reject")}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="create-panel">
          <div className="panel-title">{t("dashboard:quickCreate.title")}</div>

          <div className="panel-hint">
            {t("dashboard:quickCreate.hint")}
          </div>

          <button
            className="btn btn-secondary full"
            type="button"
            onClick={() => setLeaveModalOpen(true)}
            title={t("dashboard:quickCreate.createLeaveRequestTitle")}
          >
            {t("dashboard:quickCreate.leaveRequest")}
          </button>

          <div style={{ height: 10 }} />

          <label className="field-label">{t("dashboard:quickCreate.description")}</label>
          <textarea
            className="field-input"
            placeholder={t("dashboard:quickCreate.descriptionPlaceholder")}
            value={newTodo.description}
            onChange={(e) => setNewTodo((current) => ({
              ...current,
              description: e.target.value,
            }))}
          />

          <label className="field-label">{t("dashboard:quickCreate.assignee")}</label>
          <select
            className="field-input"
            value={newTodo.assignedTo}
            onChange={(e) => setNewTodo((current) => ({
              ...current,
              assignedTo: e.target.value,
            }))}
          >
            <option value="">{t("dashboard:quickCreate.assignTo")}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name} ({u.username})
              </option>
            ))}
          </select>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={newTodo.is_private}
              onChange={(e) => setNewTodo((current) => ({
                ...current,
                is_private: e.target.checked,
              }))}
            />
            {t("dashboard:quickCreate.private")}
          </label>

          <button
            className="btn btn-primary full"
            onClick={handleCreateTodo}
            type="button"
          >
            {t("dashboard:quickCreate.createTodo")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;



